import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="Contact lists"` — unit P14-01.
 *
 * The summary, the new-list form (`new-contact-list-panel.tsx`), and the saved lists with "Use in
 * campaign". Each saved list opens on its own page (`contact-list-detail-screen.tsx`).
 *
 * TWO PLACES THIS DIFFERS FROM THE PROTOTYPE, each because the seam or the domain says so:
 *
 *   1. **The first do-not-call rule is not a switch you can turn off.** The design draws three
 *      toggles, the first being "Skip the do-not-call list". `ContactListRuleView` carries two
 *      codes: the dialler's decision function reads no suppression option, so a customer cannot
 *      make a campaign include a suppressed number (AC-31, and the Compliance screen's promise).
 *   2. **Unreadable rows are shown.** The inspection reports them and the design does not draw
 *      them. A list that silently loses 1 row in 1,840 is a list whose count is a lie.
 *
 * AMENDED 2026-09-25 (customer-portal review, Contact lists section — "CL #1xx"): a saved list
 * opens (CL #121) and states its rules and its skipped rows (CL #120, CL #122); removing asks
 * first, naming the list, its people and when it came in, says what is deleted, and matches what
 * the API does with a list a campaign used (CL #131, CL #137, CL #139); several lists can be
 * selected and removed together (CL #138); the copy promises only what exists (CL #129, #54);
 * "New list" never throws a filled form away (CL #132); the page fits a phone (CL #133).
 *
 * AMENDED 2026-10-09 (design sync, the Claude Design export of 2026-10-09): the summary line and the
 * lede are gone from the head; a list's tag says "In 1 campaign" or "Not in a campaign" with a glyph and
 * explains itself in a tooltip (hover, focus or click; Escape closes it); the cards take the shell's card
 * radius and shadow. Opened from New campaign's "Need a different audience?", the new-list panel opens
 * at once, and saving goes back to that campaign with the new list picked (`campaign-return.ts`).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { Button, Dialog, Icon, classes, type PhosphorIcon } from "@wetalk/ui";
import { useId, useRef, useState, type ReactNode } from "react";

import "../billing/billing.css";
import "./contact-lists.css";

import { GLYPH } from "../../shell/glyph.js";
import { CAMPAIGN_GLYPH } from "../campaigns/campaign-glyph.js";
import { campaignReturnOf, withCampaignDraft, type CampaignReturn } from "../campaigns/campaign-return.js";
import { useGateway } from "../../api/provider.js";
import { QUERY_KEY, contactListQuery } from "../../api/query.js";
import type { ContactListRowView, ContactListStateView } from "../../api/gateway.js";

import {
  columnLineText,
  listStateOf,
  peopleText,
  refusalOf,
  removeBlockedText,
  removeBodyText,
  removeSeveralTitleText,
  removeTitleText,
  removedText,
  rulesText,
  skippedText,
} from "./contact-list-copy.js";
import { NewContactListPanel } from "./new-contact-list-panel.js";

const STATE_LABEL: Readonly<Record<ContactListStateView, string>> = {
  get in_use() { return portalText("operations", "in_use_20716e92"); },
  get ready() { return portalText("operations", "ready_20c7c552"); },
  get syncing() { return portalText("operations", "syncing_4ae6fa22"); },
  get failed() { return portalText("operations", "failed_09fef5d8"); },
};

/** The design's tag classes: accent for a list in use or syncing, neutral when merely ready. */
export const STATE_TONE: Readonly<Record<ContactListStateView, "accent" | "neutral" | "accent-2">> = {
  in_use: "accent",
  ready: "neutral",
  syncing: "accent",
  failed: "accent-2",
};

export { STATE_LABEL };

/**
 * The row's glyph.
 *
 * `shell/glyph.ts`'s `glyphOf` resolves the *catalogue's* icon strings and raises on anything
 * else, and a contact list is not a catalogue entry — its two icon names are not in that map.
 * Resolving them here, with the address book for a name this build has not been taught, is a
 * fallback for a GLYPH and not for a value: nothing about the row's meaning changes.
 */
function glyphForContactSource(iconName: string): PhosphorIcon {
  if (iconName.includes("arrows-clockwise")) return GLYPH.arrowsClockwise;
  if (iconName.includes("users")) return GLYPH.people;
  return GLYPH.addressBook;
}

export function ContactListScreen(): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const navigate = useNavigate();
  const location = useLocation();
  const contactList = useQuery(contactListQuery(gateway));
  // Opened from New campaign's "Need a different audience?": the panel opens at once (design 2026-10-09).
  const [campaignReturn, setCampaignReturn] = useState<CampaignReturn | null>(() => campaignReturnOf(location.state));
  const [newOpen, setNewOpen] = useState(campaignReturn !== null);
  const [notice, setNotice] = usePortalMessage();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [confirmSeveral, setConfirmSeveral] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  /** Back to New campaign as it was left, with `contactListId` picked. */
  const backToCampaign = (handOff: CampaignReturn, contactListId: string): void => {
    void navigate({
      to: "/campaigns/new",
      search: { voiceAgent: handOff.voiceAgentId, contactList: contactListId },
      state: (previous) => withCampaignDraft(previous, handOff.draft),
    });
  };

  if (contactList.isError) {
    return (
      <section className="c-page cl-page" data-screen-label="Contact lists">
        <h1 className="cl-title">{portalText("operations", "contact_lists_b2cf5d26")}</h1>
        <p className="c-note c-note-critical" role="alert">
          {portalText("operations", "your_contact_lists_could_not_be_read_so_this_page_is_showi_84107a69")}</p>
      </section>
    );
  }

  if (contactList.data === undefined) {
    return (
      <section className="c-page cl-page" data-screen-label="Contact lists">
        <h1 className="cl-title">{portalText("operations", "contact_lists_b2cf5d26")}</h1>
        <p className="c-note">{portalText("operations", "reading_your_contact_lists_07e2b45e")}</p>
      </section>
    );
  }

  const page = contactList.data;
  const chosen = page.row.filter((one) => selected.has(one.contactListId));

  return (
    <section className="c-page cl-page" data-screen-label="Contact lists">
      <div className="cl-head">
        <div className="cl-head-text">
          <h1 className="cl-title">{portalText("operations", "contact_lists_b2cf5d26")}</h1>
        </div>
        <Button
          variant="primary"
          aria-expanded={newOpen}
          onClick={() => {
            setNotice(null);
            // Open, or — when it is already open — take the member back to it. Never closes it,
            // so a filled form is not thrown away by a second click (CL #132).
            if (!newOpen) {
              setNewOpen(true);
              return;
            }
            panelRef.current?.scrollIntoView({ block: "start" });
            panelRef.current?.querySelector("input")?.focus();
          }}
        >
          <Icon glyph={GLYPH.plus} size="md" />
          {portalText("operations", "new_list_d44f1a8e")}</Button>
      </div>

      {notice === null ? null : (
        <p className="c-note cl-notice" role="status">
          {notice}
        </p>
      )}

      {newOpen ? (
        <div ref={panelRef}>
          <NewContactListPanel
            existingName={page.row.map((one) => one.name)}
            forCampaign={campaignReturn !== null}
            onBackToCampaign={() => {
              if (campaignReturn !== null) backToCampaign(campaignReturn, campaignReturn.contactListId);
            }}
            onClose={() => {
              setNewOpen(false);
              setCampaignReturn(null);
            }}
            onSaved={(saved) => {
              setNewOpen(false);
              if (campaignReturn !== null) {
                backToCampaign(campaignReturn, saved.contactListId);
                return;
              }
              setNotice(() => (portalText("operations", "saved_value_0b61d418", { v0: String(saved.name) })));
            }}
          />
        </div>
      ) : null}

      {page.row.length === 0 ? (
        newOpen ? null : (
          <div className="c-panel cl-empty">
            <p className="c-empty-title">{portalText("operations", "no_contact_lists_yet_a528cc11")}</p>
            <p className="c-empty-dek">
              {portalText("operations", "a_campaign_calls_the_people_on_a_list_upload_a_csv_with_a_985727fb")}</p>
          </div>
        )
      ) : (
        <>
          {chosen.length === 0 ? null : (
            <div className="cl-bulk" role="region" aria-label={portalText("operations", "selected_lists_b2f414e0")}>
              <span>
                {chosen.length} {portalText("operations", "selected_835f3b50")}</span>
              <Button
                variant="secondary"
                onClick={() => {
                  setConfirmSeveral(true);
                }}
              >
                {portalText("operations", "remove_selected_f38b8091")}</Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setSelected(new Set());
                }}
              >
                {portalText("operations", "clear_selection_247fd631")}</Button>
            </div>
          )}
          <div className="cl-rows">
            {page.row.map((one) => (
              <ContactListCard
                key={one.contactListId}
                row={one}
                selected={selected.has(one.contactListId)}
                onSelected={(next) => {
                  setSelected((current) => {
                    const copy = new Set(current);
                    if (next) copy.add(one.contactListId);
                    else copy.delete(one.contactListId);
                    return copy;
                  });
                }}
                onRemoved={() => {
                  setNotice(() => (removedText([one.name])));
                  setSelected((current) => {
                    const copy = new Set(current);
                    copy.delete(one.contactListId);
                    return copy;
                  });
                }}
              />
            ))}
          </div>
        </>
      )}

      <RemoveSeveralDialog
        open={confirmSeveral}
        row={chosen}
        onClose={() => {
          setConfirmSeveral(false);
        }}
        onDone={(removed, failed) => {
          setConfirmSeveral(false);
          setSelected(new Set(failed.map((one) => one.contactListId)));
          setNotice(() => {
            const said = removed.length === 0 ? [] : [removedText(removed)];
            for (const one of failed) said.push(portalText("operations", "value_was_not_removed_value_012c0c42", { v0: String(one.name), v1: String(one.reason) }));
            return said.join(" ");
          });
        }}
      />

      {/* Design sync 2026-10-09: the sidebar no longer links Do not call, and the design's summary
          figure ("41 on do not call") is gone from this head, so the foot's own words lead there. */}
      <p className="cl-foot">
        {portalText("operations", "people_who_ask_not_to_be_called_are_added_to_your_5d377afe")}<Link to="/do-not-call">{portalText("operations", "do_not_call_list_bc43a77b")}</Link>{" "}
        {portalText("operations", "automatically_and_skipped_in_every_future_campaign_92649397")}</p>
    </section>
  );
}

/** One saved list: its state, facts, rules, the way to open it, and what you can do to it. */
function ContactListCard({
  row,
  selected,
  onSelected,
  onRemoved,
}: {
  readonly row: ContactListRowView;
  readonly selected: boolean;
  readonly onSelected: (next: boolean) => void;
  readonly onRemoved: () => void;
}): ReactNode {
  usePortalTranslation("operations");
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const skipped = skippedText(row.skippedRowCount);
  const columnLine = columnLineText(row);
  const removable = row.campaignCount === 0;

  return (
    <div className="cl-row" data-selected={selected}>
      <span className="cl-select">
        <input
          type="checkbox"
          checked={selected}
          disabled={!removable}
          aria-label={portalText("operations", "select_value_94d8a34f", { v0: String(row.name) })}
          title={removable ? portalText("operations", "select_value_94d8a34f", { v0: String(row.name) }) : removeBlockedText(row.campaignCount)}
          onChange={(event) => {
            onSelected(event.currentTarget.checked);
          }}
        />
      </span>
      <span className="cl-mark">
        <Icon glyph={glyphForContactSource(row.iconName)} size="xl" />
      </span>
      <span className="cl-body">
        <span className="cl-name-line">
          <Link
            to="/contact-lists/$contactListId"
            params={{ contactListId: row.contactListId }}
            className="cl-name"
          >
            {row.name}
          </Link>
          <ListStateTag row={row} />
        </span>
        <span className="cl-meta">
          {row.metaText}
          {skipped === null ? null : <> · {skipped}</>}
        </span>
        <span className="cl-detail">
          {row.detailText} · {rulesText(row)}
          {columnLine === null ? null : <> · {columnLine}</>}
        </span>
      </span>
      <span className="cl-actions">
        <Link
          to="/campaigns/new"
          search={{ voiceAgent: "", contactList: row.contactListId }}
          className="btn btn-secondary"
        >
          {portalText("operations", "use_in_campaign_779163bc")}</Link>
        <button
          type="button"
          className="btn btn-icon"
          aria-label={portalText("operations", "options_for_value_e4ed898f", { v0: String(row.name) })}
          title={portalText("operations", "options_for_value_e4ed898f", { v0: String(row.name) })}
          aria-expanded={optionsOpen}
          onClick={() => {
            setOptionsOpen((open) => !open);
          }}
        >
          <Icon glyph={GLYPH.dotsThree} size="xl" />
        </button>
      </span>
      {optionsOpen ? (
        <div className="cl-options">
          <div className="c-row">
            <Link
              to="/contact-lists/$contactListId"
              params={{ contactListId: row.contactListId }}
              className="btn btn-secondary"
            >
              {portalText("operations", "open_rename_or_change_rules_df62557b")}</Link>
            {removable ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setConfirmOpen(true);
                }}
              >
                {portalText("operations", "remove_this_list_829650ee")}</Button>
            ) : null}
            <Button
              variant="ghost"
              onClick={() => {
                setOptionsOpen(false);
              }}
            >
              {portalText("operations", "close_bbfa773e")}</Button>
          </div>
          {removable ? null : <p className="c-empty-dek">{removeBlockedText(row.campaignCount)}</p>}
        </div>
      ) : null}
      <RemoveOneDialog
        open={confirmOpen}
        row={row}
        onClose={() => {
          setConfirmOpen(false);
        }}
        onRemoved={() => {
          setConfirmOpen(false);
          onRemoved();
        }}
      />
    </div>
  );
}

/**
 * The design's state tag (2026-10-09): "In 1 campaign" with the outgoing-call glyph, or "Not in a
 * campaign" with a tick, as a button whose tooltip says what that means — on hover, focus or click,
 * closed on leave, blur or Escape.
 */
function ListStateTag({ row }: { readonly row: ContactListRowView }): ReactNode {
  usePortalTranslation("operations");
  const tipId = useId();
  const [open, setOpen] = useState(false);
  const state = listStateOf(row);
  return (
    <span
      className="cl-state"
      onMouseEnter={() => {
        setOpen(true);
      }}
      onMouseLeave={() => {
        setOpen(false);
      }}
    >
      <button
        type="button"
        className={classes("tag", `tag-${STATE_TONE[row.state]}`, "cl-state-tag")}
        aria-describedby={open ? tipId : undefined}
        onFocus={() => {
          setOpen(true);
        }}
        onBlur={() => {
          setOpen(false);
        }}
        onClick={() => {
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
        }}
      >
        <Icon glyph={state.calling ? CAMPAIGN_GLYPH.phoneOutgoing : CAMPAIGN_GLYPH.checkCircle} size="xs" />
        {state.label}
      </button>
      {open ? (
        <span id={tipId} role="tooltip" className="cl-state-tip">
          {state.tip}
        </span>
      ) : null}
    </span>
  );
}

/** "Remove "Basic comma CSV"?" — naming the list, its people and when it came in (CL #137). */
export function RemoveOneDialog({
  open,
  row,
  onClose,
  onRemoved,
}: {
  readonly open: boolean;
  readonly row: ContactListRowView;
  readonly onClose: () => void;
  readonly onRemoved: () => void;
}): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => gateway.deleteContactList(row.contactListId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.contactList });
      onRemoved();
    },
  });

  return (
    <Dialog
      open={open}
      title={removeTitleText(row)}
      onClose={() => {
        remove.reset();
        onClose();
      }}
      actions={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              remove.reset();
              onClose();
            }}
          >
            {portalText("operations", "keep_it_d9f36924")}</Button>
          <Button
            variant="primary"
            disabled={remove.isPending}
            onClick={() => {
              remove.mutate();
            }}
          >
            {remove.isPending ? portalText("operations", "removing_579d7e74") : portalText("operations", "remove_e963907d")}
          </Button>
        </>
      }
    >
      <p>{removeBodyText(row)}</p>
      {remove.error === null ? null : (
        <p className="c-note c-note-critical" role="alert">
          {refusalOf(remove.error).message}
        </p>
      )}
    </Dialog>
  );
}

interface RemoveFailure {
  readonly contactListId: string;
  readonly name: string;
  readonly reason: string;
}

/** Several lists at once (CL #138): one confirmation naming each, then one delete per list. */
function RemoveSeveralDialog({
  open,
  row,
  onClose,
  onDone,
}: {
  readonly open: boolean;
  readonly row: readonly ContactListRowView[];
  readonly onClose: () => void;
  readonly onDone: (removed: readonly string[], failed: readonly RemoveFailure[]) => void;
}): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: async () => {
      const removed: string[] = [];
      const failed: RemoveFailure[] = [];
      for (const one of row) {
        try {
          await gateway.deleteContactList(one.contactListId);
          removed.push(one.name);
        } catch (raised) {
          failed.push({
            contactListId: one.contactListId,
            name: one.name,
            get reason() { return refusalOf(raised instanceof Error ? raised : new Error(String(raised))).message; },
          });
        }
      }
      return { removed, failed };
    },
    onSuccess: async ({ removed, failed }) => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.contactList });
      onDone(removed, failed);
    },
  });
  const people = row.reduce((total, one) => total + one.personCount, 0);

  return (
    <Dialog
      open={open}
      title={removeSeveralTitleText(row.length)}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {portalText("operations", "keep_them_40343dab")}</Button>
          <Button
            variant="primary"
            disabled={remove.isPending || row.length === 0}
            onClick={() => {
              remove.mutate();
            }}
          >
            {remove.isPending ? portalText("operations", "removing_579d7e74") : portalText("operations", "remove_value_f7ef029d", { v0: String(String(row.length)) })}
          </Button>
        </>
      }
    >
      <ul className="cl-remove-list">
        {row.map((one) => (
          <li key={one.contactListId}>
            "{one.name}" — {peopleText(one.personCount)}, {one.arrivalText}
          </li>
        ))}
      </ul>
      <p>
        {portalText("operations", "contacts.deletePeople", { count: row.length, people: peopleText(people) })}</p>
    </Dialog>
  );
}
